import { Doc, Id } from "@cvx/_generated/dataModel";

export interface Video {
  _id: Id<"contractVideos">;
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  bookmarks?: number;
  earnings?: number;
}

export interface CostBreakdown {
  flatFeePerVideo: number;
  cpmCost: number;
  timePeriodCost: number;
  total: number;
}

export interface CostCalculationResult {
  cost: number;
  predefinedCpm: number;
  hasChallengeCampaigns: boolean;
  hasNonChallengeCampaigns: boolean;
  costBreakdown: CostBreakdown;
}

export function calculateCost(
  videos: Video[],
  contracts: Doc<"creatorContracts">[],
  campaigns: Doc<"campaigns">[],
  videoToContractMap: Record<string, string>,
): CostCalculationResult {
  let cost = 0;
  let predefinedCpm = 0;
  let hasChallengeCampaigns = false;
  let hasNonChallengeCampaigns = false;
  const costBreakdown: CostBreakdown = {
    flatFeePerVideo: 0,
    cpmCost: 0,
    timePeriodCost: 0,
    total: 0,
  };

  // Create maps for easy access
  const contractMap = new Map<string, Doc<"creatorContracts">>();
  for (const contract of contracts) {
    contractMap.set(contract._id as unknown as string, contract);
  }

  const campaignMap = new Map<Id<"campaigns">, Doc<"campaigns">>();
  for (const campaign of campaigns) {
    campaignMap.set(campaign._id, campaign);
  }

  // Group videos by contract to handle "totalViews" type CPM and per-contract campaign logic
  const videosByContract = new Map<string, Video[]>();
  for (const video of videos) {
    const contractId = videoToContractMap[video._id];
    if (contractId) {
      const contractIdString = contractId as unknown as string;
      if (!videosByContract.has(contractIdString)) {
        videosByContract.set(contractIdString, []);
      }
      const contractVideosList = videosByContract.get(contractIdString);
      if (contractVideosList) {
        contractVideosList.push(video);
      }
    }
  }

  const now = Date.now();

  // Calculate cost for each contract individually
  // Each contract may belong to a different campaign with different terms
  for (const [contractIdString, contractVideos] of videosByContract.entries()) {
    const contract = contractMap.get(contractIdString);
    if (!contract) continue;

    // Get the campaign for this specific contract
    const contractCampaign = campaignMap.get(contract.campaignId);
    const isChallengeCampaign = contractCampaign?.type === "challenge";

    // Calculate this contract's campaign timeline for time period costs
    const campaignStartDate =
      contractCampaign?._creationTime ?? contract.startDate;
    const campaignEndDate = contractCampaign?.endDate ?? contract.endDate;
    const effectiveEndDate = campaignEndDate > now ? now : campaignEndDate;

    if (isChallengeCampaign && contractCampaign?.predefinedCpm) {
      // Challenge campaign: use predefinedCPM for videos in this contract
      hasChallengeCampaigns = true;
      const contractViews = contractVideos.reduce(
        (sum, video) => sum + (video.views ?? 0),
        0,
      );
      const contractCpmCost =
        (contractCampaign.predefinedCpm * contractViews) / 1000;
      costBreakdown.cpmCost += contractCpmCost;
      cost += contractCpmCost;

      // Track the predefinedCPM (use the first one found, or average if multiple)
      if (predefinedCpm === 0) {
        predefinedCpm = contractCampaign.predefinedCpm;
      }
    } else {
      // Non-challenge campaign
      hasNonChallengeCampaigns = true;
      // CiR or influencer campaign: use this contract's terms (flat fee + CPM)

      // Get flat fee per video
      const flatFeePerVideo = contract.flatFee?.perVideo ?? 0;

      // Calculate flat fee costs for all videos in this contract
      const flatFeeCost = flatFeePerVideo * contractVideos.length;
      costBreakdown.flatFeePerVideo += flatFeeCost;

      // Calculate CPM costs based on type
      let cpmCost = 0;

      if (contract.cpmRate?.type === "perVideo") {
        // Per video CPM: calculate cost for each video individually
        for (const video of contractVideos) {
          const views = video.views ?? 0;
          const threshold = contract.cpmRate.threshold ?? 0;
          const cpmRate = contract.cpmRate.rate ?? 0;
          const maxPayout = contract.cpmRate.maxPayout;

          if (views >= threshold) {
            let videoCpmCost = (cpmRate * views) / 1000;

            // Apply max payout cap if exists
            if (maxPayout) {
              videoCpmCost = Math.min(videoCpmCost, maxPayout);
            }

            cpmCost += videoCpmCost;
          }
        }
      } else if (contract.cpmRate?.type === "totalViews") {
        // Total views CPM: calculate based on total views across all videos in this contract
        const totalContractViews = contractVideos.reduce(
          (sum, video) => sum + (video.views ?? 0),
          0,
        );
        const threshold = contract.cpmRate.threshold ?? 0;
        const cpmRate = contract.cpmRate.rate ?? 0;
        const maxPayout = contract.cpmRate.maxPayout;

        if (totalContractViews >= threshold) {
          cpmCost = (cpmRate * totalContractViews) / 1000;

          // Apply max payout cap if exists
          if (maxPayout) {
            cpmCost = Math.min(cpmCost, maxPayout);
          }
        }
      }

      costBreakdown.cpmCost += cpmCost;

      // Calculate flat fee time period (retainer) based on this contract's campaign timeline
      // NOTE: This is calculated once per contract, not per video.
      // If a creator has multiple videos, they're all under the same contract,
      // so the retainer is only calculated once for that contract.
      // Use contract start date if it's later than campaign start, otherwise use campaign start
      let timePeriodCost = 0;
      if (contract.flatFee?.timePeriod) {
        const timePeriodDays = contract.flatFee.timePeriod.days;
        const timePeriodAmount = contract.flatFee.timePeriod.amount;

        // Use contract start date if it's later than campaign start, otherwise use campaign start
        const contractStartDate = contract.startDate;
        const effectiveStartDate =
          contractStartDate > campaignStartDate
            ? contractStartDate
            : campaignStartDate;

        // Calculate days elapsed from effective start to campaign end (or now)
        const contractDaysElapsed = Math.floor(
          (effectiveEndDate - effectiveStartDate) / (1000 * 60 * 60 * 24),
        );

        // Calculate how many time periods have elapsed
        if (contractDaysElapsed >= timePeriodDays && contractDaysElapsed > 0) {
          timePeriodCost =
            Math.floor(contractDaysElapsed / timePeriodDays) * timePeriodAmount;
        }
      }

      costBreakdown.timePeriodCost += timePeriodCost;

      cost += flatFeeCost + cpmCost + timePeriodCost;
    }
  }

  costBreakdown.total = cost;

  return {
    cost,
    predefinedCpm,
    hasChallengeCampaigns,
    hasNonChallengeCampaigns,
    costBreakdown,
  };
}
